/**
 * ErrorBanner - displays feedback messages (error, warning, info) with optional retry/dismiss
 */
import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';

type BannerVariant = 'error' | 'warning' | 'info';

const variantConfig: Record<
  BannerVariant,
  { bg: string; border: string; text: string; icon: typeof AlertTriangle }
> = {
  error: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-400',
    icon: AlertTriangle,
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-400',
    icon: AlertTriangle,
  },
  info: {
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    border: 'border-cyan-200 dark:border-cyan-800',
    text: 'text-cyan-700 dark:text-cyan-400',
    icon: Info,
  },
};

interface ErrorBannerProps {
  error?: string | Error | null;
  message?: string;
  variant?: BannerVariant;
  onRetry?: () => void;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  error,
  message,
  variant = 'error',
  onRetry,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const [dismissed, setDismissed] = useState(false);

  const displayMessage = message || (typeof error === 'string' ? error : error?.message);
  if (!displayMessage || dismissed) return null;

  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-4 border rounded-xl mb-4',
        config.bg,
        config.border,
        className,
      )}
    >
      <IconComponent size={20} className={cn(config.text, 'flex-shrink-0')} />
      <span className={cn('text-sm flex-1', config.text)}>{displayMessage}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
            variant === 'error' &&
              'text-red-600 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50',
            variant === 'warning' &&
              'text-amber-600 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50',
            variant === 'info' &&
              'text-cyan-600 bg-cyan-100 dark:bg-cyan-900/30 hover:bg-cyan-200 dark:hover:bg-cyan-900/50',
          )}
        >
          <RefreshCw size={14} />
          Zkusit znovu
        </button>
      )}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className={cn(
            'p-1 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5',
            config.text,
          )}
          aria-label="Zavřít"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default ErrorBanner;
